import { Injectable } from '@angular/core';

import {
  ContentMetadataService,
  OrganisedPropertyGroup,
} from '@alfresco/adf-content-services';
import { Node } from '@alfresco/js-api';
import { CardViewItem } from '@alfresco/adf-core';
import { CardViewGroup } from '@alfresco/adf-content-services/lib/content-metadata/interfaces/card-view-group.interface';
import { iif, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import { WkContentMetadataConfigFactory } from './wk-content-metadata-config.factory';
import { WkPropertyDescriptorsService } from './wk-property-descriptors.service';
import { WkPropertyGroupTranslatorService } from './wk-property-groups-translator.service';

@Injectable({
  providedIn: 'root',
})
export class WkContentMetadataService {
  private readonly transformers: ((
    items: CardViewGroup[]
  ) => Observable<CardViewGroup[]>)[] = [];
  private basicProperties = (node: Node) =>
    this.contentMetadataService.getBasicProperties(node);

  constructor(
    private readonly contentMetadataService: ContentMetadataService,
    private readonly wkContentMetadataConfigFactory: WkContentMetadataConfigFactory,
    private readonly wkPropertyDescriptorsService: WkPropertyDescriptorsService,
    private readonly wkPropertyGroupTranslatorService: WkPropertyGroupTranslatorService
  ) {}

  setTransformationForGroupedProperties(
    transformFunc: (items: CardViewGroup[]) => Observable<CardViewGroup[]>
  ) {
    this.transformers.push(transformFunc);
  }

  public getBasicProperties(node: Node): Observable<CardViewItem[]> {
    return this.basicProperties(node);
  }

  public getContentTypeProperty(node: Node): Observable<CardViewItem[]> {
    return this.contentMetadataService.getContentTypeProperty(node);
  }

  public setBasicProperties(
    callback: (node: Node) => Observable<CardViewItem[]>
  ): void {
    this.basicProperties = callback;
  }
  public getGroupedProperties(
    node: Node,
    presetName: string = null
  ): Observable<CardViewGroup[]> {
    let groupedProperties = of([]);

    if (node.aspectNames) {
      const contentMetadataConfig =
          this.wkContentMetadataConfigFactory.get(presetName),
        groupNames = node.aspectNames
          .concat(node.nodeType)
          .filter((groupName) =>
            contentMetadataConfig.isGroupAllowed(groupName)
          );

      if (groupNames.length > 0) {
        groupedProperties = this.wkPropertyDescriptorsService
          .load(groupNames)
          .pipe(
            switchMap((groups) =>
              iif(
                () => contentMetadataConfig.isIncludeAllEnabled(),
                of(
                  contentMetadataConfig
                    .appendAllPreset(groups)
                    .concat(contentMetadataConfig.reorganiseByConfig(groups))
                ),
                of(contentMetadataConfig.reorganiseByConfig(groups))
              )
            ),
            map((groups) => contentMetadataConfig.filterExcludedPreset(groups)),
            map((groups) =>
              this.contentMetadataService.filterEmptyPreset(groups)
            ),
            map((groups) =>
              this.contentMetadataService.setTitleToNameIfNotSet(groups)
            ),
            map((groups) =>
              this.wkPropertyGroupTranslatorService.translateToCardViewGroups(
                groups,
                node.properties
              )
            )
          );
      }
    }
    if (this.transformers.length) {
      let group: CardViewGroup[] = [];
      this.transformers.forEach((func) => {
        groupedProperties = groupedProperties.pipe(
          take(1),
          tap({
            next: (items: CardViewGroup[]) => (group = items),
          }),
          switchMap(func),
          catchError(() => of(group))
        );
      });
    }
    return groupedProperties;
  }

  getFolderBasicProperties(node: Node): Observable<CardViewItem[]> {
    return this.contentMetadataService.getBasicProperties(node);
  }

  public openConfirmDialog(changedProperties): Observable<any> {
    return this.contentMetadataService.openConfirmDialog(changedProperties);
  }

  public setTitleToNameIfNotSet(
    propertyGroups: OrganisedPropertyGroup[]
  ): OrganisedPropertyGroup[] {
    return this.contentMetadataService.setTitleToNameIfNotSet(propertyGroups);
  }

  public filterEmptyPreset(
    propertyGroups: OrganisedPropertyGroup[]
  ): OrganisedPropertyGroup[] {
    return this.contentMetadataService.filterEmptyPreset(propertyGroups);
  }
}
